import { FC, ReactNode, SyntheticEvent, useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface CollapsibleSectionProps {
  title: ReactNode
  defaultExpanded?: boolean
  // Controlled mode: when `expanded` is provided it wins over internal state; pair it with `onExpandedChange`
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  children: ReactNode
}

/**
 * Section wrapper that collapses its content behind a header. The body (and therefore any
 * data-fetching hooks it contains) is only mounted while expanded — `unmountOnExit` gives us
 * lazy loading for free: nothing fetches until the user opens the section.
 */
export const CollapsibleSection: FC<CollapsibleSectionProps> = ({ title, defaultExpanded = false, expanded: controlledExpanded, onExpandedChange, children }) => {
  const [internalExpanded, setInternalExpanded] = useState<boolean>(defaultExpanded)
  const expanded = controlledExpanded ?? internalExpanded

  const handleChange = (_: SyntheticEvent, isExpanded: boolean) => {
    setInternalExpanded(isExpanded)
    onExpandedChange?.(isExpanded)
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      TransitionProps={{ unmountOnExit: true }}
      disableGutters
      elevation={0}
      square
      sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0, borderBottom: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'text.secondary' } }}
      >
        {typeof title === 'string' ? <Typography variant="h6">{title}</Typography> : title}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>{children}</AccordionDetails>
    </Accordion>
  )
}
